import partition from 'lodash/partition';
import pick from 'lodash/pick';
import { createMatrixModel, getMatrixModel, updateMatrixModel } from './helpers';
import { Actions, ActionOptions } from '../constants';

const { MATRIX_MODELS } = ActionOptions;

const matrixModelValidFields = [
  'matrix_data_type',
  'x_axis_header_data_type',
  'y_axis_header_data_type',
  'x_axis_header',
  'y_axis_header',
  'matrix',
  'name',
];

// We need to keep the original actionId that was assigned to the
// new action in order to connect the matrix model to the action.
async function callCreateMatrixModel(typhonDomain, model) {
  const { actionId } = model;
  const createdMatrix = await createMatrixModel(typhonDomain, model);
  return {
    ...createdMatrix,
    actionId,
  };
}

function createMatrixModels(typhonDomain, matrixModels) {
  return Promise.all(
    matrixModels.map(model => callCreateMatrixModel(typhonDomain, model)),
  );
}

async function callUpdateMatrixModel(typhonDomain, model) {
  const { actionId, matrix_model_token: token } = model;
  const payload = pick(model, matrixModelValidFields);
  const updatedMatrix = await updateMatrixModel(typhonDomain, token, payload);
  return {
    ...updatedMatrix,
    actionId,
  };
}

function updateMatrixModels(typhonDomain, matrixModels) {
  return Promise.all(
    matrixModels.map(model => callUpdateMatrixModel(typhonDomain, model)),
  );
}

// Returns an array of action option payloads that need to be
// created for the new matrix model to work.
function createActionOptionPayloadForNewMatrix(matrixModels) {
  return matrixModels.reduce((acc, { matrix_model_token: token, version, actionId }) => {
    acc.push({
      actionId,
      key: MATRIX_MODELS.TOKEN,
      value: token,
      jsonValue: null,
    });

    acc.push({
      actionId,
      key: MATRIX_MODELS.VERSION,
      value: version,
      jsonValue: null,
    });
    return acc;
  }, []);
}

function createActionOptionPayloadForUpdatedMatrix(matrixModels) {
  return matrixModels.reduce((acc, { version, actionId }) => {
    acc[actionId] = {
      actionId,
      key: MATRIX_MODELS.VERSION,
      value: version,
      jsonValue: null,
    };
    return acc;
  }, {});
}

// Creates new matrix models and updates existing ones.
// Returns matrixModelActionOptions: an array of action options to be created
// and updatedMatrixModelActionOptions: an object of action options to be replaced.
async function saveMatrixModels(typhonDomain, matrixModels, returnModelResults = false) {
  const [existingModels, newModels] = partition(matrixModels, MATRIX_MODELS.TOKEN);

  const [updatedModels, createdModels] = await Promise.all([
    updateMatrixModels(typhonDomain, existingModels),
    createMatrixModels(typhonDomain, newModels),
  ]);

  if (returnModelResults) {
    return {
      updatedModels,
      createdModels,
    };
  }

  return {
    matrixModelActionOptions: createActionOptionPayloadForNewMatrix(createdModels),
    updatedMatrixModelActionOptions: createActionOptionPayloadForUpdatedMatrix(updatedModels),
  };
}

function findOption(actionOptions, action, key) {
  return actionOptions.find(option => option.actionId === action.id && option.key === key);
}

// TODO: handle other header types
function formatHeader(header, headerType) {
  if (headerType === 'number') {
    return header.map(Number);
  }
  return header;
}

// If the header is of type number, we should convert its values to
// numbers so we can compare them
function formatMatrixModel(matrixModel) {
  const {
    x_axis_header_data_type: xAxisHeaderDataType,
    y_axis_header_data_type: yAxisHeaderDataType,
    x_axis_header: xAxisHeader,
    y_axis_header: yAxisHeader,
  } = matrixModel;
  return {
    ...matrixModel,
    x_axis_header: formatHeader(xAxisHeader, xAxisHeaderDataType),
    y_axis_header: formatHeader(yAxisHeader, yAxisHeaderDataType),
  };
}

async function getMatrixModelFromAction(typhonDomain, action, actionOptions) {
  const tokenOption = findOption(actionOptions, action, MATRIX_MODELS.TOKEN) || {};
  const versionOption = findOption(actionOptions, action, MATRIX_MODELS.VERSION) || {};

  const matrixModel = await getMatrixModel(typhonDomain, tokenOption.value, versionOption.value);
  return {
    ...formatMatrixModel(matrixModel),
    actionId: action.id,
  };
}

async function getMatrixModels(typhonDomain, { actions, actionOptions }) {
  const matrixModelActions = actions.filter(action => action.type === Actions.MATRIX_MODEL);
  return Promise.all(
    matrixModelActions.map(action => getMatrixModelFromAction(typhonDomain, action, actionOptions)),
  );
}

export { saveMatrixModels, getMatrixModels };
